import { DEPOT_SAS_MINUTES, parseTimeToMinutes, stopWindow, travelMinutesAfter, type TourStopItem } from '../helpers';

// Layout maths of `TourTimeline`: pure functions over already-measured widths, no DOM. Label measuring stays
// in the component.

/** Numbered dot diameter, also every chip's height. */
export const DOT = 24;
export const CHIP_GAP = 3;
/** Longest a named chip may be. */
export const CHIP_LABEL_MAX_PX = 140;
/** Narrowest a truncated name may get before the lane falls back to numbers. */
export const CHIP_LABEL_MIN_PX = 52;

export type Scheduled = {
    stop: TourStopItem;
    seq: number;
    /** Drawn time: the estimate, squeezed into the tour window when the tour does not fit. */
    timeMin: number;
    /** Honest estimate, shown in the hover card. */
    estimatedMin: number;
    windowStartMin: number | null;
    windowStopMin: number | null;
    noWindow: boolean;
    /** Honest estimate lands after the window closes. */
    late: boolean;
    squeezed: boolean;
};

export type Placed = Scheduled & { leftPx: number; widthPx: number; labelled: boolean };

export type DepotMarks = { departureMin: number; roadOutMin: number; returnMin: number; roadBackMin: number };

/**
 * Widest a name may be so the whole lane fits: short names keep their width, long ones share what is left.
 * `Infinity` when nothing needs truncating.
 */
export const fittingLabelCap = (widths: number[], budget: number): number => {
    const sorted = [...widths].sort((a, b) => a - b);

    let remaining = budget;
    for (const [index, width] of sorted.entries()) {
        const cap = remaining / (sorted.length - index);
        if (width >= cap) return cap;
        remaining -= width;
    }

    return Infinity;
};

/**
 * One lane's chip placement. Chips follow the clock while they fit; otherwise the lane is spread evenly, the
 * order matters more than the exact time (which stays in the hover card).
 */
export const layoutLane = (
    schedule: Scheduled[],
    widths: number[],
    trackWidth: number,
    pxOf: (min: number) => number,
): Placed[] => {
    if (trackWidth <= 0 || schedule.length === 0) return [];

    const needed = (sizes: number[]) => sizes.reduce((sum, width) => sum + width + CHIP_GAP, -CHIP_GAP);
    const cap = fittingLabelCap(widths, trackWidth - CHIP_GAP * (schedule.length - 1));
    const labelled = cap >= CHIP_LABEL_MIN_PX;
    const sizes = labelled ? widths.map((width) => Math.floor(Math.min(width, cap))) : schedule.map(() => DOT);

    // Centred on the passage time, pushed right just enough not to touch the previous chip.
    let cursor = 0;
    const ideal = sizes.map((widthPx, index) => {
        const leftPx = Math.max(pxOf(schedule[index].timeMin) - widthPx / 2, cursor);
        cursor = leftPx + widthPx + CHIP_GAP;
        return leftPx;
    });

    // Overflow: spread evenly. `step` goes negative only for too many dots, which then overlap evenly.
    const overflows = cursor - CHIP_GAP > trackWidth;
    const free = trackWidth - needed(sizes);
    const step = schedule.length > 1 ? free / (schedule.length - 1) : 0;

    let spread = 0;
    return schedule.map((item, index) => {
        const widthPx = sizes[index];
        const leftPx = overflows ? spread : ideal[index];
        spread += widthPx + CHIP_GAP + step;
        return { ...item, leftPx, widthPx, labelled };
    });
};

/** Depot flag position: on the clock, but pinned outside the chips so a pushed lane never draws it inside. */
export const depotFlagLeftPx = ({
    clockPct,
    isReturn,
    placed,
    trackWidth,
}: {
    clockPct: number;
    isReturn: boolean;
    placed: Placed[];
    trackWidth: number;
}): number | null => {
    if (trackWidth <= 0) return null;

    const clockPx = (clockPct / 100) * trackWidth;
    const edge = isReturn ? placed[placed.length - 1] : placed[0];
    if (!edge) return clockPx;

    return isReturn
        ? Math.min(Math.max(clockPx, edge.leftPx + edge.widthPx + CHIP_GAP), trackWidth)
        : Math.max(Math.min(clockPx, edge.leftPx - CHIP_GAP), 0);
};

/**
 * Passage times from the tour start, service time + travel per stop (a task takes its time too). When the raw
 * total overflows the tour window, offsets are scaled to end on it: order and relative weight survive.
 * `estimatedMin` and `late` keep the honest values, squeezing must never hide an infeasible tour.
 */
export const buildSchedule = (stops: TourStopItem[], fallbackStart: number): Scheduled[] => {
    const starts = stops.map((stop) => parseTimeToMinutes(stopWindow(stop).start));
    const ends = stops.map((stop) => parseTimeToMinutes(stopWindow(stop).stop));
    const known = (values: (number | null)[]) => values.filter((value): value is number => value != null);
    const tourStart = known(starts).length ? Math.min(...known(starts)) : fallbackStart;
    const tourEnd = known(ends).length ? Math.max(...known(ends)) : null;

    let offset = 0;
    const offsets = stops.map((stop, index) => {
        const own = offset;
        offset += (stop.theoricServiceTime ?? 0) + travelMinutesAfter(stops, index);
        return own;
    });

    const rawSpan = offsets[offsets.length - 1] ?? 0;
    const available = tourEnd != null ? tourEnd - tourStart : null;
    const squeezed = available != null && available > 0 && rawSpan > available;
    const factor = squeezed && available != null ? available / rawSpan : 1;

    return stops.map((stop, index) => {
        const estimatedMin = tourStart + offsets[index];
        return {
            stop,
            seq: index + 1,
            timeMin: tourStart + offsets[index] * factor,
            estimatedMin,
            windowStartMin: starts[index],
            windowStopMin: ends[index],
            noWindow: starts[index] == null && ends[index] == null,
            late: ends[index] != null && estimatedMin > (ends[index] as number),
            squeezed,
        };
    });
};

/** Warehouse departure and return, from the honest schedule: sas scan + road out before, road back after. */
export const depotMarks = (schedule: Scheduled[], roadOutMin: number, roadBackMin: number): DepotMarks | null => {
    const first = schedule[0];
    const last = schedule[schedule.length - 1];
    if (!first || !last) return null;
    return {
        departureMin: first.estimatedMin - DEPOT_SAS_MINUTES - roadOutMin,
        roadOutMin,
        returnMin: last.estimatedMin + (last.stop.theoricServiceTime ?? 0) + roadBackMin,
        roadBackMin,
    };
};
